/**
 * @fileoverview Forgot Password page for requesting a reset link
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { LandingNavbar } from '@/components/layout/LandingNavbar';
import { apiService } from '@/services/api/apiService';

export const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { t } = useLanguage();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setError(t('auth.invalid_email', 'Por favor, ingrese un email válido'));
            return;
        }

        setStatus('loading');
        setError('');

        try {
            await apiService.request('/auth/forgot-password', {
                method: 'POST',
                body: JSON.stringify({ email }),
            });
            setStatus('success');
        } catch (err) {
            setError(err.message || t('auth.forgot_password_error', 'Error al procesar la solicitud'));
            setStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <LandingNavbar />

            <div className="flex-1 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-12 bg-white">
                <div className="w-full max-w-md space-y-8">
                    <div className="text-center space-y-2">
                        <h1 className="text-3xl font-extrabold text-slate-900">
                            {t('auth.forgot_password_title', 'Recuperar Contraseña')}
                        </h1>
                        <p className="text-slate-500">
                            {status === 'success'
                                ? t('auth.forgot_password_success_subtitle', 'Revisa tu correo para continuar')
                                : t('auth.forgot_password_subtitle', 'Ingresa tu email y te enviaremos un link para restablecer tu contraseña')}
                        </p>
                    </div>

                    {status === 'success' ? (
                        <div className="space-y-6 animate-fade-in-up">
                            <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-700 flex flex-col items-center text-center gap-4">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-2xl">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-1">{t('common.email_sent', 'Email enviado!')}</h3>
                                    <p className="text-sm opacity-90">
                                        {t('auth.forgot_password_check_email', 'Hemos enviado las instrucciones a {email}. No olvides revisar la carpeta de spam.').replace('{email}', email)}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full py-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-all"
                            >
                                {t('auth.back_to_login', 'Volver al inicio de sesión')}
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6 mt-8">
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 ml-1">
                                    {t('auth.email', 'Correo Electrónico')}
                                </label>
                                <div className="relative">
                                    <input
                                        type="email"
                                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 transition-all outline-none placeholder:text-slate-400"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t('auth.email_placeholder', 'nombre@empresa.com')}
                                        required
                                        autoFocus
                                        disabled={status === 'loading'}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 flex items-start gap-3 animate-fade-in-up">
                                    <i className="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
                                    <p className="text-sm font-medium">{error}</p>
                                </div>
                            )}

                            <div className="space-y-4">
                                <button
                                    type="submit"
                                    className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-bold text-lg shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
                                    disabled={status === 'loading' || !email}
                                >
                                    {status === 'loading' ? (
                                        <>
                                            <i className="fas fa-circle-notch fa-spin"></i>
                                            {t('common.sending', 'Enviando...')}
                                        </>
                                    ) : (
                                        <>
                                            <span>{t('auth.send_reset_link', 'Enviar Enlace')}</span>
                                            <i className="fas fa-paper-plane"></i>
                                        </>
                                    )}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => navigate('/login')}
                                    className="w-full py-4 text-slate-500 text-sm font-semibold hover:text-slate-800 transition-colors"
                                    disabled={status === 'loading'}
                                >
                                    {t('common.cancel', 'Cancelar')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};
